import {AuiIf, ThreadPrimitive, useAui} from '@assistant-ui/react';
import {Button, CalloutIcon, DotProgress, Link} from '@wandb/components';
import React, {type FC, useCallback, useEffect, useRef, useState} from 'react';
import {useDropzone} from 'react-dropzone';

import {
  useActiveThread,
  useActiveThreadLoadState,
  useIsOnNewChat,
} from '../../agentChat/store/context';
import {useDismissibleNotice} from '../../agentChat/useDismissibleNotice';
import {useReloadThread} from '../../agentChat/useReloadThread';
import {ErrorBoundary} from '../ErrorBoundary';
import {ACCEPTED_IMAGE_MEDIA_TYPES} from './attachments/imageParts';
import {useComposerImageUpload} from './attachments/useComposerImageUpload';
import {AGENT_NAME} from './constants';
import {AgentSuggestionList} from './suggestions/AgentSuggestionList';
import {WBAgentComposer} from './WBAgentComposer';
import {
  WBAgentDebugViewProvider,
  WBAgentDebugViewSlot,
} from './WBAgentDebugView';
import {AssistantMessage} from './WBAgentMessageAssistant';
import {UserMessage} from './WBAgentMessageUser';

const messageComponents = {UserMessage, AssistantMessage};

const WBAGENT_DEMO_VIDEO_URL = 'https://www.youtube.com/watch?v=F3xwB3GQKkk';

export const WBAgentThread = () => {
  const {activeThreadId, isLoading, failure} = useActiveThreadLoadState();
  const activeThread = useActiveThread();
  const isOnNewChat = useIsOnNewChat();

  const aui = useAui();
  const reloadThread = useReloadThread();

  const {dismissed: specialWelcomeDismissed, dismiss: dismissSpecialWelcome} =
    useDismissibleNotice('wbagent-special-welcome');
  const [isFirstVisit] = useState(() => !specialWelcomeDismissed);
  useEffect(() => {
    if (isFirstVisit) {
      dismissSpecialWelcome();
    }
  }, [isFirstVisit, dismissSpecialWelcome]);

  // Window-wide image drop zone. `noClick`/`noKeyboard` keep the picker from
  // opening on stray clicks; the composer's "Add image" menu triggers `open`.
  const {onDrop} = useComposerImageUpload();
  const {getRootProps, getInputProps, isDragActive, open} = useDropzone({
    onDrop,
    accept: ACCEPTED_IMAGE_MEDIA_TYPES,
    noClick: true,
    noKeyboard: true,
  });

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <DotProgress aria-label="Loading thread" />
      </div>
    );
  }

  if (failure?.kind === 'error' && activeThreadId) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-12 px-16">
        <CalloutIcon icon="warning" color="red" size="md" />
        <span className="font-semibold text-base">Failed to load chat</span>
        <Button
          variant="secondary"
          startIcon="reload-refresh"
          onPress={() => void reloadThread(activeThreadId)}>
          Retry
        </Button>
      </div>
    );
  }

  if (failure?.kind === 'not-found') {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-12 px-16">
        <CalloutIcon icon="search" color="moon" size="md" />
        <span className="font-semibold text-base">Couldn't find this chat</span>
        <Button
          variant="primary"
          startIcon="compose"
          onPress={() => aui.threads().switchToNewThread()}>
          Start a new chat
        </Button>
      </div>
    );
  }

  const emptyThreadError =
    activeThread &&
    activeThread.messageTree.rootChildren.length === 0 &&
    activeThread.error;

  return (
    <WBAgentDebugViewProvider>
      <div
        {...getRootProps()}
        className="relative flex flex-1 min-h-0 flex-col">
        <input {...getInputProps()} />
        {isDragActive && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/90 text-xl font-medium text-moon-800 py-16">
            <div className="flex flex-col items-center gap-16">
              <CalloutIcon icon="photo-picture" color="teal" />
              Drop images here to add to chat
            </div>
          </div>
        )}
        <ThreadPrimitive.Root
          className="[container-type:inline-size] flex flex-1 min-h-0 flex-col"
          style={{
            ['--thread-max-width' as string]: '44rem',
          }}>
          <ThreadPrimitive.ViewportProvider>
            <ThreadPrimitive.Viewport
              turnAnchor="top"
              autoScroll
              className="flex flex-1 flex-col overflow-y-auto scroll-smooth overscroll-contain pl-24 pr-24 pt-12 [scrollbar-gutter:stable] [scrollbar-width:thin] [scrollbar-color:#0d0f1252_transparent]">
              <WBAgentDebugViewSlot>
                <MessageRecoveryBoundary>
                  {emptyThreadError ? (
                    <EmptyThreadError message={emptyThreadError} />
                  ) : (
                    <AuiIf condition={s => s.thread.isEmpty}>
                      <div className="flex flex-1 flex-col items-start justify-center gap-12 mt-24 mb-24 text-left w-full max-w-[420px] [@container(min-width:699px)]:max-w-[900px] self-center">
                        <div
                          className={
                            isFirstVisit
                              ? 'flex flex-col items-start gap-8'
                              : 'flex items-center gap-8 self-center'
                          }>
                          <span className="flex flex-wrap gap-x-[0.25em] text-xl font-semibold">
                            {isFirstVisit ? (
                              <>
                                <span>Hi, I'm {AGENT_NAME},</span>
                                <span>your ML research assistant</span>
                              </>
                            ) : (
                              'How can I help you today?'
                            )}
                          </span>
                        </div>
                        {isFirstVisit && (
                          <div className="flex flex-col gap-8">
                            <span className="text-pretty text-moon-650 text-sm [&_a]:!text-sm">
                              I write code, run experiments, visualize your
                              data, and surface patterns, so you can wake up to
                              a better model.{' '}
                              <Link
                                href={WBAGENT_DEMO_VIDEO_URL}
                                target="_blank"
                                rel="noopener noreferrer">
                                Learn more
                              </Link>
                            </span>
                          </div>
                        )}
                        {isOnNewChat && <AgentSuggestionList />}
                      </div>
                    </AuiIf>
                  )}
                  <div className="flex flex-col shrink-0">
                    <ThreadPrimitive.Messages components={messageComponents} />
                  </div>
                </MessageRecoveryBoundary>
              </WBAgentDebugViewSlot>
            </ThreadPrimitive.Viewport>
            <div className="px-24 py-16">
              <WBAgentComposer onStartAddImage={open} />
            </div>
          </ThreadPrimitive.ViewportProvider>
        </ThreadPrimitive.Root>
      </div>
    </WBAgentDebugViewProvider>
  );
};

function EmptyThreadError({message}: {message: string}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-12 px-16 text-center">
      <CalloutIcon icon="warning" color="moon" size="md" />
      <span className="max-w-[360px] text-sm text-moon-650">{message}</span>
    </div>
  );
}

/**
 * Auto-recovers from AUI tearing crashes during thread switches [1] by
 * remounting children with fresh subscriptions. Allows one recovery per thread
 * switch; a second error re-throws from `renderError` (error boundaries cannot
 * catch their own render errors) so it bubbles to `AgentChatProvider`.
 *
 * [1] https://github.com/assistant-ui/assistant-ui/issues/3652
 */
const MessageRecoveryBoundary: FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const {activeThreadId} = useActiveThreadLoadState();
  const [recoveryKey, setRecoveryKey] = useState(0);
  const recovered = useRef(false);

  // Reset on thread switch — uses the store value (tick 1), which updates
  // before AUI processes the committed switch (tick 2).
  const prevThreadId = useRef(activeThreadId);
  if (prevThreadId.current !== activeThreadId) {
    prevThreadId.current = activeThreadId;
    recovered.current = false;
  }

  const handleError = useCallback(() => {
    if (recovered.current) {
      return;
    }

    recovered.current = true;

    // Wait one frame so AUI's microtask scheduler finishes flushing before
    // remounting children with new subscriptions.
    requestAnimationFrame(() => setRecoveryKey(k => k + 1));
  }, []);

  const renderError = useCallback((error: Error) => {
    if (recovered.current) {
      throw error; // Propagate a second error up to the next boundary.
    }

    return <></>;
  }, []);

  return (
    <ErrorBoundary
      key={recoveryKey}
      silent
      onError={handleError}
      renderError={renderError}>
      {children}
    </ErrorBoundary>
  );
};
